import {
  ElementController,
  cycleValues,
  getData,
  getWindowWidth,
  parseHTML,
  setData,
} from 'js-common';

import { INTRO_CONTROLLER_ID } from './constants';

// Keep duration in sync with _intro.scss
const ANIMATION_DURATION = 4000;

const VIDEO_SMALL_THRESHOLD = 800;
const ACTIVE_ANIMATION_CLASS = 'intro-image-active';
const ANIMATION_DIRECTIONS_DATA_ATTR = 'direction';
const ANIMATION_DIRECTIONS = [
  'top-left',
  'top-right',
  'bottom-right',
  'bottom-left',
];

/**
 * Controller for intro area.
 */
export default class Intro extends ElementController {
  static id = INTRO_CONTROLLER_ID;

  /**
   * DOM and images loaded, instantiate slider.
   */
  load() {
    // Has slider root and at least one additional image, do slider
    this.sliderRoot = this.getSingleNode('slider-root');
    this.additionalSliderItems = this.getNodes('intro-item');

    if (this.sliderRoot && this.additionalSliderItems.length) {
      this.loadSlider();
    }

    // Has video, load the relevant size
    const videoEl = this.getSingleNode('video-root');
    if (videoEl) {
      const dataSrc =
        getWindowWidth() <= VIDEO_SMALL_THRESHOLD
          ? 'video-small'
          : 'video-large';
      const source = parseHTML(
        `<source type="video/mp4" src="${getData(videoEl, dataSrc)}">`,
      );
      videoEl.append(source);
    }
  }

  /**
   * Load and initialize Flickity.
   */
  async loadSlider() {
    const flickityFade = await import(
      /* webpackChunkName: 'flickity-fade' */ 'flickity-fade'
    );
    const Flickity = flickityFade.default;

    // Insert the rest of the slides. The ideal way would be to lazily insert
    // each next slide on the slider's change event until all have been added,
    // but for some reason the fade animation breaks when doing that.
    this.additionalSliderItems.forEach((script) => {
      const newItem = parseHTML(script.innerHTML);
      this.sliderRoot.append(newItem);
      script.remove();
    });

    // Init slider
    this.slider = new Flickity(this.sliderRoot, {
      accessibility: false,
      autoPlay: ANIMATION_DURATION,
      draggable: false,
      fade: true,
      pageDots: false,
      pauseAutoPlayOnHover: false,
      prevNextButtons: false,
    });

    // Set class for animation, change when the active slide changes
    [this.currentDirection] = ANIMATION_DIRECTIONS;
    this.setActiveSlide(this.slider.cells[0].element);
    this.slider.on('change', this.handleSliderChange);
    this.slider.on('settle', this.handleSliderSettle);
  }

  /**
   * Set the active slide for animation purposes.
   *
   * @param {Element} slide - The slide DOM node.
   */
  setActiveSlide(slide) {
    setData(slide, ANIMATION_DIRECTIONS_DATA_ATTR, this.currentDirection);
    this.currentDirection = cycleValues(
      this.currentDirection,
      ...ANIMATION_DIRECTIONS,
    );
    slide.classList.add(ACTIVE_ANIMATION_CLASS);
  }

  /**
   * Unset an active slide so that it starts properly when being set again.
   *
   * @param {Element} slide - The slide DOM node.
   */
  unsetActiveSlide(slide) {
    slide.classList.remove(ACTIVE_ANIMATION_CLASS);
  }

  /**
   * Set the active slide when it changes.
   */
  handleSliderChange = () => {
    this.setActiveSlide(this.slider.selectedElement);
  };

  /**
   * Unset the previously active slide when the fade is completed.
   */
  handleSliderSettle = () => {
    this.unsetActiveSlide(this.slider.cells[this.slider.fadeHideIndex].element);
  };
}
